import { useAuth } from '@/context/AuthContext';
import { useCallback, useEffect, useState } from 'react';
import api from '@/lib/api';

/*
 * useSubscription
 *
 * Single source of truth for subscription/billing state on the frontend.
 *
 * Reads the subscription snapshot the backend embeds in /auth/me. Provides a
 * lightweight refresh() that re-fetches /api/billing/status (cheaper than
 * /auth/me since it skips loading the user document). Also exposes derived
 * UI flags: shouldShowTrialBanner, isCriticalTrialDay, etc.
 *
 * The hook is intentionally read-mostly; mutations (checkout, portal) live
 * in their own helpers so this can be re-used anywhere without coupling.
 */
export function useSubscription() {
  const { user, refresh: refreshUser } = useAuth();
  const [sub, setSub] = useState(user?.subscription || null);
  const [refreshing, setRefreshing] = useState(false);

  // Keep local state in sync with AuthContext whenever the user object changes
  useEffect(() => {
    setSub(user?.subscription || null);
  }, [user]);

  const refresh = useCallback(async () => {
    setRefreshing(true);
    try {
      const { data } = await api.get('/billing/status');
      setSub(data);
      // Also nudge AuthContext so other consumers stay in sync.
      try { await refreshUser(); } catch (_) {
        // Non-fatal — local sub state already updated above
      }
      return data;
    } finally {
      setRefreshing(false);
    }
  }, [refreshUser]);

  const status = sub?.status;
  const isPro = !!sub?.is_pro;
  const isTrialing = !!sub?.is_trialing;
  const isLocked = !!sub?.is_locked;
  const canScan = !!sub?.can_scan;
  const daysLeft = sub?.trial_days_remaining;
  const graceDaysLeft = sub?.grace_days_remaining;

  // Banner only when trialing AND <=6 days left (it auto-hides once paid).
  const shouldShowTrialBanner = isTrialing && daysLeft != null && daysLeft <= 7;
  // Severity escalates as the trial nears its end.
  const trialSeverity = (() => {
    if (!isTrialing || daysLeft == null) return 'info';
    if (daysLeft <= 1) return 'critical';
    if (daysLeft <= 2) return 'warning';
    return 'info';
  })();

  return {
    sub,
    status,
    isPro,
    isTrialing,
    isLocked,
    canScan,
    daysLeft,
    graceDaysLeft,
    shouldShowTrialBanner,
    trialSeverity,
    refresh,
    refreshing,
  };
}

export default useSubscription;
